import { CheckCircleOutlined, ErrorOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { find } from 'lodash';
import {
    Datagrid,
    DateField,
    FunctionField,
    List,
    LoadingIndicator,
    ReferenceManyField,
    TextField,
    TextInput,
    useNotify,
    usePermissions,
    useTranslate,
    useUpdate,
    WithListContext,
} from 'react-admin';
import { Outlet, useNavigate } from 'react-router';
import useGetCurrentUser from '@/hooks/useGetCurrentUser';
import {
    ASSIGN_CLIENT_ROUTE,
    ASSIGN_SITE_ROUTE,
    BIG_AGENCY_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    BIG_CLIENT_ADMIN,
    BIG_OPERATOR,
    BIG_SUB_MANAGER_CLIENT_TAG,
    BIG_SUB_MANAGER_SITE_TAG,
    MANAGER_PLACES,
    MANAGER_TAG,
    ROLE_BIG_LINK_USER_TO_CLIENT,
    ROLE_BIG_LINK_USER_TO_SITE,
    ROLE_DELETE_USER,
    SUB_MANAGER_TAG,
    USER_PROFILES,
    USERS,
} from '@/utils/CONST';
import extractIdFromURI from '@/utils/id';
import CustomBlock from '@/components/common/custom-block/custom-block';
import SwitchComponent from '@/components/common/custom-fields/switch-component';
import UserActionField from '@/components/common/custom-fields/user-action-field';
import ListStylesContainer from '@/components/common/list-container/list-styles-container';
import UsersListActions from '@/components/users/users-list-actions';

const UsersListMain = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const [update] = useUpdate();
    const notify = useNotify();
    useGetCurrentUser();

    const navigateToAssignClient = (userId: null | string) => navigate(`${ASSIGN_CLIENT_ROUTE}/${userId}`);
    const navigateToAssignSite = (userId: null | string) => navigate(`${ASSIGN_SITE_ROUTE}/${userId}`);

    const handleEnableUser = ({ target: { checked }, id }: any) => {
        update(
            USERS,
            {
                id,
                data: {
                    enabled: checked,
                },
            },
            {
                mutationMode: 'optimistic',
                onSuccess: () => {
                    notify(checked ? 'messages.user.activated' : 'messages.user.deactivated', {
                        type: 'success',
                        messageArgs: { smart_count: 1 },
                    });
                },
                onError: () => {
                    notify('messages.error', { type: 'error' });
                },
            },
        );
    };

    return (
        <CustomBlock label={translate('main.navigation.users')}>
            <ListStylesContainer>
                <List
                    actions={<UsersListActions />}
                    component='div'
                    resource={USERS}
                    filter={{
                        'tags[all]': [
                            BIG_SUB_MANAGER_CLIENT_TAG,
                            BIG_SUB_MANAGER_SITE_TAG,
                            MANAGER_TAG,
                            SUB_MANAGER_TAG,
                        ],
                        hidden: false,
                        isSemiAnonymous: false,
                    }}
                    sort={{ order: 'DESC', field: 'createdAt' }}
                    empty={false}
                    storeKey='managerUsersList'
                    filters={filters}
                >
                    <Datagrid bulkActionButtons={false}>
                        <TextField label={'main.navigation.last_name'} source='lastName' />

                        <TextField source='firstName' label={'main.navigation.name'} />

                        <TextField label={'main.navigation.email'} source='email' />

                        {/*<BooleanField label={'users.list.enabled'} source={'enabled'} />*/}

                        <DateField
                            label={'main.navigation.last_login'}
                            options={{ year: 'numeric', month: '2-digit', day: '2-digit' }}
                            source={'lastLogin'}
                        />

                        <FunctionField
                            label={'users.list.user_type'}
                            render={({ tags }: any) => {
                                const aux = find(tags, (tag: string) =>
                                    USER_PROFILES.includes(tag.toUpperCase()),
                                )?.toLowerCase();
                                return (
                                    <Typography sx={{ color: !aux ? 'red' : 'black' }}>
                                        {aux
                                            ? translate(`users.profiles.${aux}`)
                                            : 'BIG_CLIENT_SITE_OPERATOR_CREATE_SITE'}
                                    </Typography>
                                );
                            }}
                        />

                        <FunctionField
                            label={'users.list.association'}
                            render={({ tags, id }: { tags: string[]; id: string }) => {
                                const sub_manager_type = find(tags, (tag) =>
                                    [BIG_SUB_MANAGER_CLIENT_TAG, BIG_SUB_MANAGER_SITE_TAG, BIG_OPERATOR, BIG_CLIENT_ADMIN].includes(tag),
                                );
                                const allowed =
                                    (permissions.includes(ROLE_BIG_LINK_USER_TO_SITE) ||
                                        permissions.includes(ROLE_BIG_LINK_USER_TO_CLIENT)) &&
                                    !(
                                        tags.includes(BIG_AGENCY_MANAGER) ||
                                        tags.includes(BIG_AGENCY_OPERATOR) ||
                                        tags.includes(BIG_AGENCY_ADMIN)
                                    );

                                if (!!sub_manager_type && allowed)
                                    return (
                                        <ReferenceManyField reference={MANAGER_PLACES} target={'userManager'}>
                                            <WithListContext
                                                render={({ data }) =>
                                                    data === undefined ? (
                                                        <LoadingIndicator />
                                                    ) : (
                                                        <Box sx={{ alignItems: 'center', gap: 1, display: 'flex' }}>
                                                            {data?.length > 0 ? (
                                                                <Tooltip title={translate('messages.user_icon_green')}>
                                                                    <CheckCircleOutlined color={'success'} />
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip title={translate('messages.user_icon_red')}>
                                                                    <ErrorOutlined color={'error'} />
                                                                </Tooltip>
                                                            )}
                                                            <Button
                                                                size={'small'}
                                                                onClick={
                                                                    sub_manager_type === BIG_SUB_MANAGER_CLIENT_TAG ||
                                                                    sub_manager_type === BIG_CLIENT_ADMIN
                                                                        ? () =>
                                                                              navigateToAssignClient(
                                                                                  extractIdFromURI(id),
                                                                              )
                                                                        : () =>
                                                                              navigateToAssignSite(extractIdFromURI(id))
                                                                }
                                                            >
                                                                {translate(
                                                                    sub_manager_type === BIG_SUB_MANAGER_CLIENT_TAG ||
                                                                    sub_manager_type === BIG_CLIENT_ADMIN
                                                                        ? 'buttons.add_client_access'
                                                                        : 'buttons.add_site_access',
                                                                )}
                                                            </Button>
                                                        </Box>
                                                    )
                                                }
                                            />
                                        </ReferenceManyField>
                                    );
                                else return '--';
                            }}
                        />

                        <FunctionField
                            label={'users.list.enabled'}
                            render={({ enabled, id }: { enabled: boolean; id: string }) => (
                                <SwitchComponent
                                    data-cy='activate-switch'
                                    checked={enabled}
                                    onChange={(e) => {
                                        e.stopPropagation();
                                        handleEnableUser({ target: { checked: e.target.checked }, id });
                                    }}
                                />
                            )}
                        />

                        {permissions?.includes(ROLE_DELETE_USER) && (
                            <Box display={'flex'} alignItems={'center'} gap={2}>
                                <FunctionField render={({ id }: any) => <UserActionField userId={id} />} />
                            </Box>
                        )}
                    </Datagrid>
                </List>
            </ListStylesContainer>

            <Outlet />
        </CustomBlock>
    );
};

const filters = [
    <TextInput source='lastName' label={'main.navigation.last_name'} variant='standard' resettable />,
    <TextInput source='firstName' label={'main.navigation.name'} variant='standard' resettable />,
    <TextInput source='email' label='main.navigation.email' variant='standard' resettable />,
];

export default UsersListMain;
